import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Sparkles, ExternalLink } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { homeTarget, ShareHomeButton } from '@/components/ShareHomeButton';
/**
 * SharedRecapPage — public SPA route for shared Weekly Recap.
 *
 * Path: /share/recap/:shareId
 *
 * Renders the FROZEN snapshot of the user's recap at the time it was
 * shared (topics + date range). Tapping "Open in Eili" sends viewers
 * into the in-app /recap route (protected — will redirect to /login if
 * unauthenticated).
 */
export default function SharedRecapPage() {
  const {
    t
  } = useTranslation();

  const { shareId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${window.location.origin}/api/recap/share/${shareId}`)
      .then((r) => { if (alive) setDoc(r.data); })
      .catch(() => { if (alive) setError('This shared recap is unavailable.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ background: '#0f172a' }} data-testid="shared-recap-loading">
        <div className="w-8 h-8 border-2 border-t-transparent border-indigo-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center text-center p-4"
           style={{ background: '#0f172a', color: '#f1f5f9' }}
           data-testid="shared-recap-error">
        <p className="text-lg font-semibold mb-2">{t('generated.sharedrecappage_recap_unavailable_f2f2')}</p>
        <p className="text-sm opacity-70 mb-6">{error || 'This link may have expired.'}</p>
        <button
          onClick={() => navigate(homeTarget(), { replace: true })}
          className="px-4 py-2 rounded-full text-sm font-medium"
          style={{ background: '#6366f1', color: '#fff' }}
        >
          {t('generated.sharedrecappage_open_eili_4165')}
        </button>
      </div>
    );
  }

  const topics = doc.topics || [];

  return (
    <div className="min-h-dvh px-4 py-10" style={{ background: '#0f172a', color: '#f1f5f9' }} data-testid="shared-recap-page">
      <ShareHomeButton />
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(homeTarget(), { replace: true })}
          className="inline-flex items-center gap-2 text-sm opacity-75 mb-6 hover:opacity-100"
          data-testid="shared-recap-back"
        >
          <ArrowLeft className="h-4 w-4" /> {t('generated.sharedrecappage_back_ea75')}
        </button>

        <div
          className="rounded-3xl p-8 sm:p-10 shadow-2xl"
          style={{
            background: 'linear-gradient(180deg, rgba(30,41,59,0.85) 0%, rgba(15,23,42,0.85) 100%)',
            border: '1px solid rgba(99,102,241,0.25)',
            backdropFilter: 'blur(8px)',
          }}
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wider mb-3"
               style={{ background: 'rgba(99,102,241,0.15)', color: '#a5b4fc' }}>
            <Sparkles className="h-3.5 w-3.5" />
            {t('generated.sharedrecappage_weekly_recap_694f')}
          </div>

          {/* Date range */}
          {doc.date_range && (
            <p className="text-sm opacity-70 mb-2" data-testid="shared-recap-date-range">{doc.date_range}</p>
          )}

          {/* Headline */}
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight mb-6" data-testid="shared-recap-headline">
            {doc.headline || 'What I studied this week'}
          </h1>

          {/* Topic chips */}
          {topics.length > 0 ? (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="shared-recap-topics">
              {topics.map((t, i) => (
                <span
                  key={`${t.label}-${i}`}
                  className="px-3 py-1.5 rounded-full text-sm font-medium"
                  style={{ background: 'rgba(99,102,241,0.15)', color: '#c7d2fe' }}
                >
                  {t.label}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm opacity-60 mb-6">{t('generated.sharedrecappage_no_topics_in_fe54')}</p>
          )}

          {/* Footer CTA */}
          <div className="mt-8 pt-6 flex items-center justify-between"
               style={{ borderTop: '1px solid rgba(148,163,184,0.15)' }}>
            <span className="text-sm italic opacity-70">{t('generated.sharedrecappage_eili_a572')}</span>
            <button
              onClick={() => navigate('/recap')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors hover:scale-[1.02]"
              style={{ background: '#6366f1', color: '#fff' }}
              data-testid="shared-recap-open-in-app"
            >
              {t('generated.sharedrecappage_see_your_own_84bf')} <ExternalLink className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
